import React, { useState, useCallback, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BROADCAST_STATUS,
  globalStyles,
  PAGE_KEYS,
  noop,
} from "@client/shared/constants";
import { formatInTimeZone } from "date-fns-tz";
import { Dropdown, FilterComponent } from "@client/shared/components";
import { AppContext } from "@client/shared/contexts";
import { AppService } from "@client/shared/services";
import {
  decodeToken,
  exportToFile,
  getToken,
  queryStringBuilder,
  getPageConfig,
  setPageConfig,
  updatePagination,
  getUserData,
} from "@client/utils";
import DashboardView from "./view";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Service from "./service";
import style from "./style";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditIcon from "@mui/icons-material/Edit";

let gridHelper = null;
const defaultState = {
  entries: [],
  entriesCount: 0,
  selectedRows: [],
  areAllSelected: false,
  pageSize: 5,
  pageNumber: 1,
  order: null,
  orderBy: "",
  totalRows: 0,
  filters: [],
  isFetching: false,
  isExporting: false,
  isFormOpen: false,
  deletingIds: null,
  rowBeingEdited: null,
  showDetails: false,
};

const Dashboard = ({
  handleJwksResponse = noop
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const userData = (getUserData() || {})?.user || {};
  const pageConfig = getPageConfig(PAGE_KEYS.BROADCASTS);
  const [state, setState] = useState({
    ...defaultState,
    pageSize: pageConfig?.pageSize || defaultState.pageSize,
    filters: params?.id
    ? [{
      field: "id",
      type: "=",
      value: params?.id
    }]
    : defaultState.filters
  });
  const { appData, setAppData } = useContext(AppContext);

  const fetchEntries = useCallback(
    async ({
      pageNumber = 1,
      pageSize = 5,
      order = "",
      orderBy = "",
      filters = [],
      extensionList = [],
      voicemailList = [],
    }) => {
      setState((prevState) => ({
        ...prevState,
        isFetching: true,
      }));
      const updatedFilters = filters.map(filter => ({
        ...filter,
        value: filter.field === 'status' ? (Object.entries(BROADCAST_STATUS).find(([key, value]) => value === filter.value) || [])[0] : filter.value,
      }));
      const queryString = queryStringBuilder(
        pageSize,
        (pageNumber - 1) * pageSize,
        [],
        updatedFilters,
        { field: orderBy, order }
      );

      const { data, error } = await Service.getBroadcasts(queryString);
      if (error) {
        setState((prevState) => ({
          ...prevState,
          isFetching: false,
        }));
        toast.error(Array.isArray(error) ? error[0].message : error.message);
      } else {
        setState((prevState) => {
          const updatedEntries = (
            data?.broadcastVoicemail || defaultState.entries
          ).map((entry) => {
            const recipientsDetails = entry.recipients.map((recipientId) => ({
              id: recipientId,
              name: (
                extensionList.find(
                  (extension) => extension.id == recipientId
                ) || {}
              ).name,
            }));
            return {
              ...entry,
              recipientsDetails,
              voicemailDetails: voicemailList.find(
                (item) => item.id === entry.libraryId
              ),
            };
          });
          return {
            ...prevState,
            isFetching: false,
            entries: updatedEntries,
            entriesCount: data?.count || defaultState.entriesCount,
          };
        });
      }
      return data || {};
    },
    []
  );

  const handleExport = async (sortObj = null, filters = []) => {
    if (!state.entries.length) {
      return toast.error("No data to export");
    }
    setState((prevState) => ({
      ...prevState,
      isExporting: true,
    }));
    const updatedFilters = filters.map(filter => ({
      ...filter,
      value: filter.field === 'status' ? (Object.entries(BROADCAST_STATUS).find(([key, value]) => value === filter.value) || [])[0] : filter.value,
    }));
    let queryString = queryStringBuilder(
      0,
      0,
      [],
      updatedFilters,
      sortObj
    );

    queryString = `${(queryString || '').includes('?') ? '' : '?'}${queryString}&export=true&timezone=${appData?.accountInfo?.regionalSettings?.timezone?.name}`;
    if (state.selectedRows.length && !state.areAllSelected) {
      queryString += `&ids=${state.selectedRows.join(',')}`;
    }
    const { data, error } = await Service.getBroadcasts(queryString);
    setState((prevState) => ({
      ...prevState,
      isExporting: false,
    }));
    if (error) {
      return toast.error(
        Array.isArray(error) ? error[0].message : error.message
      );
    } else {
      exportToFile(data, "braoadcasts");
    }
  };

  const handleDeleteClose = () => {
    setState((prevState) => ({
      ...prevState,
      deletingIds: null,
    }));
  };

  const handleDelete = async (ids = "") => {
    setState((prevState) => ({
      ...prevState,
      isDeleting: true,
    }));
    const updatedFilters = state.filters.map(filter => ({
      ...filter,
      value: filter.field === 'status' ? (Object.entries(BROADCAST_STATUS).find(([key, value]) => value === filter.value) || [])[0] : filter.value,
    }));
    const queryString = queryStringBuilder(
      0,
      0,
      [],
      [
        ...updatedFilters,
        ids === "ALL" ? [] : { field: "id", type: "in", value: ids },
      ]
    );
    const { error } = await Service.deleteBroadcast(queryString);
    if (error) {
      toast.error(
        Array.isArray(error) ? error[0].message : error.message || error
      );
    } else {
      toast.success(
        ` ${
          ids.length > 1 || ids === "ALL" ? "Broadcasts" : "Broadcast"
        } deleted successfully.`
      );
      fetchEntries({
        pageNumber: state.pageNumber,
        pageSize: state.pageSize,
        order: state.order,
        orderBy: state.orderBy,
        filters: state.filters,
        extensionList: appData.extensionList,
        voicemailList: appData.voicemailList,
      }).then((data) => {
        updatePagination({
          count: data?.count,
          pageNumber: state.pageNumber,
          pageSize: state.pageSize,
          callback: (pageNumber) =>
            setState((prevState) => ({ ...prevState, pageNumber })),
        });
      });
      setState((prevState) => ({
        ...prevState,
        selectedRows: [],
        areAllSelected: false,
        deletingIds: null,
      }));
      gridHelper && gridHelper.resetSelection();
    }
    setState((prevState) => ({
      ...prevState,
      isDeleting: false,
    }));
  };

  const fetchVoicemails = async () => {
    const response = await AppService.getVoicemails();
    let voicemailList = [];
    handleJwksResponse(response, (data) => (voicemailList = data || []));
    setAppData("voicemailList", voicemailList);
  };

  const handleFormOpen = async (
    open = false,
    isSubmitted = false,
    rowBeingEdited = null,
    showDetails = false,
  ) => {
    setState((prevState) => ({
      ...prevState,
      isFormOpen: open,
      rowBeingEdited: rowBeingEdited,
      showDetails: showDetails,
    }));
    if (params?.id && !open) {
      setState((prevState) => ({
        ...prevState,
        filters: defaultState.filters,
      }));
      navigate('/voicemails');
    }
    if (isSubmitted) {
      await fetchVoicemails();
      await fetchEntries({
        pageNumber: state.pageNumber,
        pageSize: state.pageSize,
        order: state.order,
        orderBy: state.orderBy,
        filters: state.filters,
        extensionList: appData.extensionList,
        voicemailList: appData.voicemailList,
      });
    }
  };

  const handlePageNumberChange = useCallback((pageNumber) => {
    setState((prevState) => ({
      ...prevState,
      pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPageConfig(PAGE_KEYS.BROADCASTS, {
      pageSize,
    });
    setState((prevState) => ({
      ...prevState,
      pageSize,
      pageNumber: defaultState.pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleSelectionChange = useCallback((selectedRows) => {
    setState((prevState) => ({
      ...prevState,
      selectedRows,
    }));
    // gridHelper && gridHelper.resetAllSelection();
  }, []);

  const handleSelectAll = useCallback((areAllSelected) => {
    setState((prevState) => ({
      ...prevState,
      areAllSelected,
      selectedRows: defaultState.selectedRows,
    }));
  }, []);

  const handleSortChange = useCallback((fieldObj, order) => {
    setState((prevState) => ({
      ...prevState,
      order,
      orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);
  const handleFilterChange = useCallback((filters = []) => {
    setState((prevState) => ({
      ...prevState,
      filters,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleGridReady = (instance) => {
    gridHelper = instance;
  };

  const filterConfig = [
    {
      field: "broadcastTitle",
      fieldToDisplay: "Broadcast Title",
      operatorType: "string",
    },
    {
      field: "createdBy",
      fieldToDisplay: "Created By",
      operatorType: "string",
    },
    {
      field: "status",
      fieldToDisplay: "Status",
      operatorType: "number",
      options: Object.values(BROADCAST_STATUS)?.map((status) => ({
        label: status,
        value: status,
      })),
    },
  ];

  const columnConfig = [
    {
      id: "broadcastTitle",
      field: "broadcastTitle",
      label: "BROADCAST TITLE",
      canSort: true,
      noResize: false,
      render: (row) => {
        return (
          <Tooltip title={row.broadcastTitle || ""} placement="top-start">
            <Typography
              sx={{ ...globalStyles.cursor.pointer }}
              noWrap
              variant="body2"
              onClick={() => handleFormOpen(true, false, row, true)}
            >
              {row.broadcastTitle || "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "releaseDate",
      field: "releaseDate",
      label: "RELEASE DATE",
      canSort: true,
      noResize: false,

      render: (row) => {
        const timezone = appData?.accountInfo?.regionalSettings?.timezone?.name;
        const releaseDate =
          row.releaseDate && timezone
            ? formatInTimeZone(
                row.releaseDate,
                timezone,
                "MM-dd-yyy hh:mm:ss a"
              )
            : "-";
        return (
          <Tooltip
            title={`${releaseDate + " " + timezone || ""}`}
            placement="top-start"
          >
            <Typography noWrap variant="body2">
              {releaseDate || "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "audio_recording",
      field: "audio_recording",
      label: "AUDIO RECORDING",
      headerClassName: style.audioHeader,
      // noResize: false,
      render: (row) => {
        const token = decodeToken(getToken());
        const url = `${row.voicemailDetails?.contentUri}?access_token=${token.rc_access_token}`;
        return (
          <Box
            sx={{
              ...style.audioPlayerWrapper,
            }}
          >
            {row.voicemailDetails ? (
              <audio controls src={url} style={{ width: '100%' }}/>
            ): (
              <Box
                sx={{ ...style.audioExpired, p: 0.8 }}
              >
                <Typography variant="body2">
                  Audio File Expired
                </Typography>
              </Box>
            )}
            
          </Box>
        );
      },
    },
    {
      id: "recipients",
      field: "recipients",
      label: "RECIPIENTS",
      noResize: false,
      render: (row) => {
        const recipientsName = row.recipientsDetails
          .map((item) => item.name)
          .join(", ");
        return (
          <Box
            sx={{
              ...style.recipientsTooltipWrapper,
            }}
          >
            <Tooltip
              title={row.isAllRecipients ? "All Extensions" : (recipientsName || "")}
              placement="top-start"
              PopperProps={{
                disablePortal: true,
              }}
            >
              <Typography noWrap variant="body2">
                {row.isAllRecipients ? "All Extensions" : recipientsName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: "createdBy",
      field: "createdBy",
      label: "CREATED BY",
      noResize: false,
      canSort: true,
      render: (row) => {
        const createdBy = `${row.createdBy?.firstName} ${row.createdBy?.lastName}`;
        return (
          <Tooltip title={createdBy} placement="top-start">
            <Typography noWrap variant="body2">
              {createdBy}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "status",
      field: "status",
      label: "STATUS",
      canSort: true,
      noResize: false,
      render: (row) => {
        return (
          <Tooltip
            title={BROADCAST_STATUS[row.status] || ""}
            placement="top-start"
          >
            <Typography
              noWrap
              variant="caption"
              sx={{
                ...(row.status === "SCHEDULED"
                  ? style.colorOrange
                  : row.status === "COMPLETED"
                  ? style.colorGreen
                  : row.status === "IN_PROGRESS"
                  ? style.colorYellow
                  : (row.status === "COMPLETED_WITH_ERROR" || row.status === "FAILED")
                  ? style.colorRed
                  : ""),
              }}
            >
              {BROADCAST_STATUS[row.status]}
            </Typography>
          </Tooltip>
        );
      },
    },

    {
      id: "col-action",
      label: "",
      renderHeader: () => (
        <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.end,
          }}
        >
          <Tooltip title="Export" placement="top-start">
            <IconButton
              disabled={state.isExporting}
              sx={{ mr: 0 }}
              onClick={() =>
                handleExport(
                  { field: state.orderBy, order: state.order },
                  state.filters
                )
              }
            >
              <img src="/assets/export.svg" alt="Export" />
            </IconButton>
          </Tooltip>
          <FilterComponent
            filterConfig={filterConfig}
            onFilterApply={handleFilterChange}
            appliedFilters={state.filters}
          />
        </Box>
      ),
      render: (row) => {
        let options = [
          {
            id: 1,
            label: "Details",
            icon: <ContentCopyIcon fontSize="small" />,
            onClick: () => {
              handleFormOpen(true, false, row, true);
            },
          },
          {
            id: 2,
            label: "Edit",
            icon: <EditIcon fontSize="small" />,
            onClick: () => handleFormOpen(true, false, row),
          },
          {
            id: 3,
            label: "Delete",
            icon: <HighlightOffIcon fontSize="small" />,
            onClick: () => {
              if (userData.deletePreference) {
                handleDelete([row.id]);
              } else {
                setState((prevState) => ({
                  ...prevState,
                  deletingIds: [row.id],
                }));
              }
            },
          },
        ].filter((option) => {
          if (option.id === 2 || option.id === 3) {
            if (
              option.id === 2 &&
              ((row.status || "").toLowerCase() === "completed"  ||  (row.status || "").toLowerCase() === "completed_with_error" || (row.status || "").toLowerCase() === "failed")
            ) {
              return false;
            }
            return !!userData?.roleAccess?.sendBroadcasts;
          }
          return true;
        });
        return (
          <Box
            sx={{
              ...globalStyles.display.flex,
              ...globalStyles.flex.justify.end,
              mr: 1,
            }}
          >
            <Dropdown
              options={options}
              classes={{ moreIcon: style.moreIcon }}
            />
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (appData.extensionList && appData.voicemailList) {
      fetchEntries({
        pageNumber: state.pageNumber,
        pageSize: state.pageSize,
        order: state.order,
        orderBy: state.orderBy,
        filters: state.filters,
        extensionList: appData.extensionList,
        voicemailList: appData.voicemailList,
      }).then((data) => {
        updatePagination({
          count: data?.count,
          pageNumber: state.pageNumber,
          pageSize: state.pageSize,
          callback: (pageNumber) =>
            setState((prevState) => ({ ...prevState, pageNumber })),
        });
        if (params?.id && data?.broadcastVoicemail?.length) {
          handleFormOpen(true, false, data?.broadcastVoicemail[0], true);
        }
      });
    } else {
      setState((prevState) => ({
        ...prevState,
        isFetching: true,
      }));
    }
  }, [
    state.pageNumber,
    state.pageSize,
    state.order,
    state.orderBy,
    state.filters,
    appData.extensionList,
    appData.voicemailList,
  ]);

  return (
    <DashboardView
      entries={state.entries}
      entriesCount={state.entriesCount}
      isFetching={state.isFetching}
      columnConfig={columnConfig}
      pageSize={state.pageSize}
      pageNumber={state.pageNumber}
      order={state.order}
      orderBy={state.orderBy}
      selectedRows={state.selectedRows}
      areAllSelected={state.areAllSelected}
      isDeleting={state.isDeleting}
      isFormOpen={state.isFormOpen}
      voicemailList={appData.voicemailList || []}
      siteList={appData.siteList || []}
      departmentList={appData.departmentList || []}
      userRoleList={appData.userRoleList || []}
      individualContactList={appData.individualContactList || []}
      extensionList={appData.extensionList || []}
      phoneNumberList={appData.phoneNumberList || []}
      countryList={appData.countryList || []}
      accountInfo={appData.accountInfo || {}}
      deletingIds={state.deletingIds}
      rowBeingEdited={state.rowBeingEdited}
      showDetails={state.showDetails}
      roleAccess={userData?.roleAccess}
      handlePageNumberChange={handlePageNumberChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChange={handleSortChange}
      handleSelectionChange={handleSelectionChange}
      handleFormOpen={handleFormOpen}
      handleDelete={handleDelete}
      handleDeleteClose={handleDeleteClose}
      handleSelectAll={handleSelectAll}
      handleGridReady={handleGridReady}
      setState={setState}
      userData={userData}
      handleJwksResponse={handleJwksResponse}
    />
  );
};

export default Dashboard;
